import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'urql';
import { useForm } from 'react-hook-form';

import Modal from 'ui/components/Modal';
import { useSiteConfig } from 'hooks/useSiteConfig';
import {
  SEARCH_ORDER_BY_EMAIL_PHONE_QUERY,
  SEARCH_ORDER_BY_EMAIL_QUERY,
  SEARCH_ORDER_BY_EMAIL_POSTCODE_QUERY,
  SEARCH_ORDER_BY_PHONE_QUERY,
} from 'gql/queries/order';
import { getSalesforceId } from 'utils/getSalesforceId';
import { SUPPORTED_COUNTRY } from 'utils/constants';
import { formatOrderLookupField } from 'utils/orders';
import CloseButton from 'ui/components/CloseButton';

import { CustomForm } from '../custom/CustomForm';

import {
  formFieldUnion,
  OrderByEmailPhoneFormsFields,
  OrderByPhoneFormFields,
  OrderByPostcodeEmailFormFields,
  OrderSearchOperationName,
  OrderSearchQueryResultUnion,
  SearchOrderByEmailResult,
  SearchOrderByEmailPhoneResult,
  SearchOrderByEmailPostcodeResult,
  SearchOrderbyPhoneResult,
} from './types';

export const getOrderSearchQuery = (
  orderLookUpQuery: OrderSearchOperationName
) => {
  switch (orderLookUpQuery) {
    case OrderSearchOperationName['SearchOrderByEmailPhone']:
      return SEARCH_ORDER_BY_EMAIL_PHONE_QUERY;
    case OrderSearchOperationName['SearchOrderByEmail']:
      return SEARCH_ORDER_BY_EMAIL_QUERY;
    case OrderSearchOperationName['SearchOrderByEmailPostcode']:
      return SEARCH_ORDER_BY_EMAIL_POSTCODE_QUERY;
    case OrderSearchOperationName['SearchOrderByPhone']:
      return SEARCH_ORDER_BY_PHONE_QUERY;
    default:
      return SEARCH_ORDER_BY_EMAIL_POSTCODE_QUERY;
  }
};

export const OrderLookUp = () => {
  const router = useRouter();
  const {
    countryCode,
    language,
    env,
    localizeUrlPath,
    orderLookUpQuery,
    staticFeatures: { allowGuestAccessToOrderDetails },
    forms,
    validation: {
      phone: { prefix },
    },
  } = useSiteConfig();

  const { postal, email, oid, mobileNumber } = router.query as {
    email: string;
    oid: string;
    postal: string;
    mobileNumber: string;
  };

  const [shouldShowReturnsModal, setShouldShowReturnsModal] = useState(false);
  const [formData, setFormData] = useState<formFieldUnion>(
    {
      email: '',
      phone: '',
      orderNo: '',
    } || {
        email: '',
        orderNo: '',
        postcode: '',
      } || {
        email: '',
        orderNo: '',
      } || {
        phone: '',
        orderNo: '',
      }
  );

  const orderSearchQuery = getOrderSearchQuery(orderLookUpQuery);

  const [orderDetails, lookupOrder] = useQuery<OrderSearchQueryResultUnion>({
    query: orderSearchQuery,
    variables: {
      ...formData,
    },
    pause: !formData.orderNo,
  });

  const displayReturnModal = useCallback(() => {
    setShouldShowReturnsModal(!!orderDetails.data);
  }, [orderDetails]);

  const redirectToOrderPage = useCallback(() => {
    const orderData =
      (orderDetails.data as SearchOrderbyPhoneResult)?.searchOrderByPhone ||
      (orderDetails.data as SearchOrderByEmailPhoneResult)
        ?.searchOrderByEmailPhone ||
      (orderDetails.data as SearchOrderByEmailPostcodeResult)
        ?.searchOrderByEmailPostcode ||
      (orderDetails.data as SearchOrderByEmailResult)?.searchOrderByEmail;

    if (orderData) {
      const paramFields = {
        email: (formData as OrderByEmailPhoneFormsFields).email,
        mobileNumber: (
          formData as OrderByEmailPhoneFormsFields | OrderByPhoneFormFields
        ).phone,
        postcode: (formData as OrderByPostcodeEmailFormFields).postcode,
      };

      const params = Object.keys(paramFields)
        .filter(key => paramFields[key] !== undefined)
        .map(key => `${key}=${encodeURIComponent(paramFields[key])}`)
        .join('&');

      const baseUrl = localizeUrlPath(`account/orders/${formData.orderNo}`);
      router.push(`${baseUrl}?${params}`);
    }
  }, [formData, localizeUrlPath, orderDetails, router]);

  useEffect(() => {
    if (allowGuestAccessToOrderDetails) {
      redirectToOrderPage();
    } else {
      displayReturnModal();
    }
  }, [allowGuestAccessToOrderDetails, redirectToOrderPage, displayReturnModal]);

  const formatFields = data => {
    const orderNo = formatOrderLookupField('orderNo', data.orderNo) as string;
    const email = formatOrderLookupField('email', data.orderEmail) as string;
    const postcode = formatOrderLookupField(
      'postcode',
      data.billingPostalCode,
      countryCode as SUPPORTED_COUNTRY
    ) as string;
    switch (orderLookUpQuery) {
      case OrderSearchOperationName['SearchOrderByPhone']:
        return {
          orderNo,
          phone: data.phone,
        };
      case OrderSearchOperationName['SearchOrderByEmail']:
        return {
          orderNo,
          email,
        };
      case OrderSearchOperationName['SearchOrderByEmailPostcode']:
        return {
          orderNo,
          email,
          postcode,
        };
      case OrderSearchOperationName['SearchOrderByEmailPhone']:
        return {
          orderNo,
          email,
          phone: data.phone,
        };

      default:
        // PNA order search as the default
        return {
          orderNo,
          email,
          postcode,
        };
    }
  };

  const onSubmit = async data => {
    const fields = formatFields(data);
    setFormData(fields);
    lookupOrder({ requestPolicy: 'network-only' });
  };

  const form = useForm({
    defaultValues: {
      orderNo: oid,
      postalCode: postal,
      orderEmail: email,
      phone: mobileNumber ?? prefix,
    },
    mode: 'onChange',
  });

  const site = getSalesforceId(SUPPORTED_COUNTRY[countryCode.toUpperCase()]);
  const postcode = formatOrderLookupField(
    'postcode',
    (formData as OrderByPostcodeEmailFormFields)?.postcode,
    countryCode as SUPPORTED_COUNTRY
  );

  const formEmail = (formData as OrderByPostcodeEmailFormFields)?.email;
  const encodedEmail = encodeURIComponent(formEmail).replace(/\./g, '%2e'); // encode email and replace all periods with the ASCII equivalent
  const encodedPostcode = encodeURI(postcode ?? '');

  return (
    <>
      <CustomForm
        form={form}
        onSubmit={onSubmit}
        result={orderDetails}
        config={forms.orderLookUpReturns}
      />

      {!allowGuestAccessToOrderDetails && shouldShowReturnsModal && (
        <Modal
          variant="dark"
          className="w-full max-w-screen-lg h-screen max-h-[60vh]"
        >
          <CloseButton
            className="absolute right-0 top-0 p-3"
            onClick={() => setShouldShowReturnsModal(false)}
          />
          <iframe
            className="w-full h-full"
            src={`${
              env.returnUrl
            }/Sites-${site}-Site/${language}_${countryCode.toUpperCase()}/ReturnCenter-NewReturn?oid=${
              formData.orderNo
            }&email=${encodedEmail}&postal=${encodedPostcode}&iframe`}
          />
        </Modal>
      )}
    </>
  );
};
